import { useContext, useEffect, useRef, useState } from "react";

import { FormProvider } from "react-hook-form";

import Box from "@/components/Box";
import DataLoader from "@/components/DataLoader";
import PartnerDashboardBox from "@/components/boxes/PartnerDashboardBox";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { theme } from "@/core/theme";
import { SAVE_PATIENT_PROFILE_MUTATION } from "@/graphql/accounts";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import useDocTitle from "@/hooks/use-doc-title";
import Badge from "@/tpo/Badge";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import DateInput from "@/tpo/DateInput";
import EditFormButtons from "@/tpo/EditFormButtons";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import Group from "@/tpo/Group";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import { ViewerContext } from "@/tpo/Viewer";
import isProxyEmail from "@/utils/isProxyEmail";
import isUnderEighteen from "@/utils/isUnderEighteen";
import { gql } from "@apollo/client";
import styled from "styled-components";

import { DutchTestQuestionsFieldIfFemale } from "../../DutchTestQuestions";
import Errors from "../../Errors";
import { FadeTransition } from "../../animations/FadeTransition";
import PatientDetailsSaveConfirmation from "../../organisations/shared/PatientDetailsSaveConfirmation";
import { CMP_PERMISSION_EDIT_PATIENTS } from "../../organisations/constants";
import Template from "../shared/Template";
import Modal from "@/tpo/Modal";

const GET_PROFILE_QUERY = gql`
  query GetProfileQuery($userId: ID!, $organisationId: ID!) {
    user(userId: $userId, organisationId: $organisationId) {
      id
      email
      firstName
      lastName
      isUserActive
      patientProfile {
        id
        birthControl
        postMenopausal
        hrt
        height
        weight
        dateOfBirth
        gender
        goals {
          id
          text
        }
        dietPreferences {
          id
          name
        }
        guardian {
          id
          user {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

const FormButtonsContainer = styled(Box)`
  a,
  button {
    align-self: flex-start;
  }
`;

function Profile({
  id,
  firstName,
  lastName,
  email,
  weight,
  height,
  dateOfBirth,
  gender,
  dietPreferences,
  goals,
  birthControl,
  postMenopausal,
  hrt,
  guardianEmail,
  guardianFirstName,
  guardianLastName,
  isUserActive
}) {
  const submitButtonRef = useRef();
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const { organisationId, permissions } = useOrganisationContext();

  //   if (!underEighteen) {
  //   variables = {
  //     ...variables,
  //     guardianFirstName: undefined,
  //     guardianLastName: undefined,
  //     guardianEmail: undefined
  //   };
  // } else {
  //   variables = {
  //     ...variables,
  //     email: ""
  //   };
  // }

  const canEdit = permissions.includes(CMP_PERMISSION_EDIT_PATIENTS) && !isUserActive;
  const shouldConfirmPatientDetailsSave = permissions.includes(CMP_PERMISSION_EDIT_PATIENTS);

  const { methods, onSubmit, nonFieldError, loading } = useDjangoGraphqlForm({
    mutation: SAVE_PATIENT_PROFILE_MUTATION,
    mutationName: "savePatientProfileMutation",
    defaultValues: {
      id,
      firstName,
      lastName,
      email,
      weight: weight || "",
      height: height || "",
      birthControl,
      postMenopausal,
      hrt,
      gender,
      dateOfBirth,
      guardianFirstName: guardianFirstName || "",
      guardianLastName: guardianLastName || "",
      guardianEmail: guardianEmail || "",
      organisation: organisationId
    },
    transformer: input => {
      const dob = input.dateOfBirth === "--" ? "" : input.dateOfBirth || "";

      if (dob && isUnderEighteen(dob)) {
        return {
          ...input,
          dateOfBirth: dob,
          email: ""
        };
      } else {
        return {
          ...input,
          dateOfBirth: dob
        };
      }
    },
    handleSuccess: ({ data, api: { reset } }) => {
      setShowSaveConfirmation(false);
      reset({
        id: data.savePatientProfileMutation.patientProfile.user.id,
        firstName: data.savePatientProfileMutation.patientProfile.user.firstName,
        lastName: data.savePatientProfileMutation.patientProfile.user.lastName,
        email: data.savePatientProfileMutation.patientProfile.user.email,
        weight: data.savePatientProfileMutation.patientProfile.weight || "",
        height: data.savePatientProfileMutation.patientProfile.height || "",
        gender: data.savePatientProfileMutation.patientProfile.gender,
        dateOfBirth: data.savePatientProfileMutation.patientProfile.dateOfBirth,
        birthControl: data.savePatientProfileMutation.patientProfile.birthControl,
        postMenopausal: data.savePatientProfileMutation.patientProfile.postMenopausal,
        hrt: data.savePatientProfileMutation.patientProfile.hrt,
        guardianFirstName:
          data.savePatientProfileMutation.patientProfile.guardian?.user?.firstName || "",
        guardianLastName:
          data.savePatientProfileMutation.patientProfile.guardian?.user?.lastName || "",
        guardianEmail:
          data.savePatientProfileMutation.patientProfile.guardian?.user?.email || "",
        organisation: organisationId
      });
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
      }
    },
    handleFailure: () => {
      setShowSaveConfirmation(false);
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  const { reset } = methods;

  useEffect(() => {
    if (id) {
      reset({
        id,
        firstName,
        lastName,
        email,
        weight: weight || "",
        height: height || "",
        gender,
        dateOfBirth,
        birthControl,
        postMenopausal,
        hrt,
        guardianEmail: guardianEmail || "",
        guardianFirstName: guardianFirstName || "",
        guardianLastName: guardianLastName || "",
        organisation: organisationId
      });
    }
  }, [
    reset,
    id,
    firstName,
    lastName,
    email,
    weight,
    height,
    dateOfBirth,
    gender,
    birthControl,
    postMenopausal,
    hrt,
    guardianEmail,
    guardianFirstName,
    guardianLastName,
    organisationId
  ]);

  const watchDateOfBirth = methods.watch("dateOfBirth");
  const underEighteen = watchDateOfBirth ? isUnderEighteen(watchDateOfBirth) : undefined;

  return (
    <>
      <Box px={20}>
        <PartnerDashboardBox
          mt={theme.spacing.section.pt}
          mb={theme.spacing.section.pb}
          pt={theme.spacing.figma.medium}
          pb={theme.spacing.figma.large}
        >
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Patient details
          </Box>
          <FormProvider {...methods} submitButtonRef={submitButtonRef}>
            <Stack
              as="form"
              gap={[20, 20, 40]}
              onSubmit={methods.handleSubmit(data => {
                if (shouldConfirmPatientDetailsSave) {
                  if (submitButtonRef.current) {
                    submitButtonRef.current.setPending(false);
                  }
                  setShowSaveConfirmation(true);
                  return;
                }
                return onSubmit(data);
              })}
            >
              <ControlledFormField
                name="firstName"
                label="First name"
                Component={FloatingLabelInput}
                editable={!canEdit ? !firstName : true}
              />
              <ControlledFormField
                name="lastName"
                label="Last name"
                Component={FloatingLabelInput}
                editable={!canEdit ? !lastName : true}
              />
              <FadeTransition in={!underEighteen}>
                <ControlledFormField
                  name="email"
                  label="Email"
                  Component={FloatingLabelInput}
                  editable={!canEdit ? !email || isProxyEmail(email) : true}
                />
              </FadeTransition>
              <Stack gap={[20, 20, 40]}>
                <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]}>
                  Physical details
                </Box>
                <ControlledFormField
                  Component={FloatingLabelInput}
                  label="Height"
                  name="height"
                  editable={!canEdit ? !height : true}
                />
                <ControlledFormField
                  Component={FloatingLabelInput}
                  label="Weight"
                  name="weight"
                  editable={!canEdit ? !weight : true}
                />
              </Stack>
              <ControlledFormField
                label="Date of birth"
                labelProps={{
                  fontFamily: "gilroyBold",
                  fontSize: 18,
                  mb: 20
                }}
                name="dateOfBirth"
                Component={DateInput}
                getDay={dateStr => (dateStr !== null ? dateStr.split("-")?.[2] || "" : "")}
                getMonth={dateStr => (dateStr !== null ? dateStr.split("-")?.[1] || "" : "")}
                getYear={dateStr => (dateStr !== null ? dateStr.split("-")?.[0] || "" : "")}
                constructDate={({ day, month, year }) => {
                  return `${year}-${month}-${day}`;
                }}
                disabled={!canEdit ? !!dateOfBirth : false}
              />
              <FadeTransition in={underEighteen}>
                <Box fontFamily="gilroyBold" fontSize={18} lineHeight="36px" color="red">
                  User is under 18 years old
                </Box>
                <Spacer py={15} />
                <Box fontFamily="gilroyMedium" fontSize={16}>
                  Because this user is a minor please enter the details of the legal guardian that
                  will be responsible for them.
                </Box>
                <Spacer py={15} />
                <Stack gap={20}>
                  <ControlledFormField
                    name="guardianFirstName"
                    label="Guardian first name"
                    Component={FloatingLabelInput}
                    editable={!canEdit ? !guardianFirstName && underEighteen : underEighteen}
                  />
                  <ControlledFormField
                    name="guardianLastName"
                    label="Guardian last name"
                    Component={FloatingLabelInput}
                    editable={!canEdit ? !guardianLastName && underEighteen : underEighteen}
                  />
                  <ControlledFormField
                    name="guardianEmail"
                    label="Guardian email"
                    Component={FloatingLabelInput}
                    editable={!canEdit ? !guardianEmail && underEighteen : underEighteen}
                  />
                </Stack>
              </FadeTransition>
              <FormControl
                label={
                  <Box as="h2" fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="36px">
                    Gender
                  </Box>
                }
              >
                <ControlledRadioGroup
                  Container={Stack}
                  containerProps={{
                    gap: 20
                  }}
                  name="gender"
                  values={[
                    {
                      label: "Male",
                      value: "Male"
                    },
                    {
                      label: "Female",
                      value: "Female"
                    }
                  ]}
                  disabled={!canEdit ? !!gender : false}
                />
              </FormControl>
              <DutchTestQuestionsFieldIfFemale />
              {methods.formState.isDirty && (
                <FormButtonsContainer
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  gap={20}
                  justifyContent={["flex-start", "flex-start", "center"]}
                >
                  <EditFormButtons />
                </FormButtonsContainer>
              )}
              {nonFieldError && <Errors>{nonFieldError}</Errors>}
            </Stack>
          </FormProvider>
        </PartnerDashboardBox>
      </Box>
      <Modal
        maxWidth={1024}
        closeButton={false}
        bg="white"
        show={showSaveConfirmation}
        close={() => setShowSaveConfirmation(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
        data-component-name="PatientDetailsSaveConfirmationModal"
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          <PatientDetailsSaveConfirmation
            loading={loading}
            onCancel={() => setShowSaveConfirmation(false)}
            onSave={() => methods.handleSubmit(onSubmit)()}
          />
        </Box>
      </Modal>
      {(dietPreferences?.length || goals?.length) && (
        <PartnerDashboardBox borderRadius={0} mt="auto" maxWidth={null}>
          {!!goals?.length && (
            <Stack gap={[20, 20, 40]}>
              <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                Goals
              </Box>
              <Group gap={10} flexWrap="wrap">
                {goals.map(g => (
                  <Badge bg="green" color="white" size="md" borderRadius={5} key={g.id} py={2}>
                    {g.text}
                  </Badge>
                ))}
              </Group>
            </Stack>
          )}
          {!!dietPreferences?.length && (
            <Stack gap={[20, 20, 40]}>
              <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                Diet preferences
              </Box>
              <Group gap={10} flexWrap="wrap">
                {dietPreferences.map(dp => (
                  <Badge bg="green" color="white" size="md" borderRadius={5} key={dp.id} py={2}>
                    {dp.name}
                  </Badge>
                ))}
              </Group>
            </Stack>
          )}
        </PartnerDashboardBox>
      )}
    </>
  );
}

export default function ProfileTab({ templateProps }) {
  const { userId } = useContext(ViewerContext);
  useDocTitle("Profile", false, "Regenerus");

  const { organisationId } = useOrganisationContext();

  return (
    <Template
      selectedTab="profile"
      docTitle="Patient's profile"
      {...templateProps}
      bodyProps={{
        as: Stack,
        gap: 0
      }}
    >
      <DataLoader
        query={GET_PROFILE_QUERY}
        variables={{
          userId,
          organisationId
        }}
        skip={!organisationId}
        render={({ user }) =>
          user ? (
            <Profile
              id={user.id}
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              height={user.patientProfile.height}
              weight={user.patientProfile.weight}
              dateOfBirth={user.patientProfile.dateOfBirth}
              gender={user.patientProfile.gender}
              goals={user.patientProfile.goals}
              dietPreferences={user.patientProfile.dietPreferences}
              birthControl={user.patientProfile.birthControl}
              postMenopausal={user.patientProfile.postMenopausal}
              hrt={user.patientProfile.hrt}
              guardianEmail={user.patientProfile.guardian?.user?.email}
              guardianFirstName={user.patientProfile.guardian?.user?.firstName}
              guardianLastName={user.patientProfile.guardian?.user?.lastName}
              isUserActive={user.isUserActive}
            />
          ) : null
        }
      />
    </Template>
  );
}
